<template>
    <div class="composer">
        <p v-if="recorderMessage" class="composer__notice">
            {{ recorderMessage }}
        </p>

        <div v-if="isRecording" class="composer__recording">
            <span class="composer__dot"></span>
            <span class="composer__timer">{{ elapsed }}</span>
            <AssistantLevelBars :meter="recorder.meter" />
            <v-btn small text color="#55595c" @click="cancelRecording">
                {{ $t('pages.assistant.composer.cancel') }}
            </v-btn>
            <v-btn small dark color="#967adc" @click="stopAndSend">
                {{ $t('pages.assistant.composer.stop') }}
            </v-btn>
        </div>

        <div v-else class="composer__row">
            <v-textarea
                v-model="text"
                class="composer__field"
                rows="1"
                auto-grow
                dense
                outlined
                hide-details
                :maxlength="maxLength"
                :label="$t('pages.assistant.composer.placeholder')"
                dir="auto"
                @keydown.enter.exact.prevent="send"
            ></v-textarea>
            <v-btn
                icon
                color="#967adc"
                :disabled="isSending"
                :title="$t('pages.assistant.composer.record')"
                @click="startRecording"
            >
                <v-icon>mdi-microphone</v-icon>
            </v-btn>
            <v-btn
                icon
                color="#967adc"
                :disabled="!canSend"
                :title="$t('pages.assistant.composer.send')"
                @click="send"
            >
                <v-icon class="composer__send-icon">mdi-send</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
    assistantActionTypes,
    assistantGetterTypes,
} from '@/store/modules/assistant/types'
import { ASSISTANT_TEXT_MAX_LENGTH } from '@/consts/assistant'
import audioRecorder from '@/helpers/audio-recorder'
import AssistantLevelBars from './assistant-level-bars.vue'

export default {
    name: 'AssistantComposer',
    components: { AssistantLevelBars },
    data() {
        return {
            text: '',
            maxLength: ASSISTANT_TEXT_MAX_LENGTH,
            recorder: null,
            recorderError: '',
            seconds: 0,
            ticker: null,
        }
    },
    computed: {
        ...mapGetters('assistant', {
            isSending: assistantGetterTypes.GET_IS_SENDING,
        }),
        isRecording: function () {
            return !!this.recorder
        },
        canSend: function () {
            return !this.isSending && !!this.text.trim()
        },
        elapsed: function () {
            const minutes = Math.floor(this.seconds / 60)
            const rest = this.seconds % 60

            return `${minutes}:${String(rest).padStart(2, '0')}`
        },
        recorderMessage: function () {
            if (!this.recorderError) return ''

            return this.$t(`pages.assistant.recorder.${this.recorderError}`)
        },
    },
    // The linter asks for `beforeUnmount`, which does not exist on this Vue 2.6
    // runtime and would never fire. Closing the panel mid-recording destroys
    // this component, and without a real hook the microphone would stay open.
    // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
    beforeDestroy() {
        this.cancelRecording()
    },
    methods: {
        ...mapActions('assistant', {
            sendTurn: assistantActionTypes.SEND_TURN,
        }),
        send: async function () {
            if (!this.canSend) return

            const text = this.text.trim()

            // Cleared so the optimistic bubble is the only place it appears,
            // then put back if the turn never landed.
            this.text = ''

            const answer = await this.sendTurn({ text })

            if (!answer && !this.text.trim()) this.text = text
        },
        startRecording: async function () {
            this.recorderError = ''

            try {
                this.recorder = await audioRecorder.startRecording()
                this.seconds = 0
                this.ticker = setInterval(() => {
                    this.seconds += 1
                }, 1000)
            } catch (error) {
                this.recorderError = error?.reason || 'unknown'
                this.recorder = null
            }
        },
        stopAndSend: async function () {
            const recorder = this.recorder

            if (!recorder) return

            this.stopTicker()
            this.recorder = null

            try {
                const audio = await recorder.stop()

                // Never alongside text: sending both is refused.
                this.sendTurn({ audio })
            } catch (error) {
                this.recorderError = error?.reason || 'unknown'
            }
        },
        cancelRecording: function () {
            this.stopTicker()

            if (this.recorder) {
                this.recorder.cancel()
                this.recorder = null
            }
        },
        stopTicker: function () {
            if (this.ticker) {
                clearInterval(this.ticker)
                this.ticker = null
            }
        },
    },
}
</script>

<style scoped lang="scss">
.composer {
    padding: 8px 16px 12px;
    border-top: 1px solid #eceeef;
    background: $color-primary-light;

    &__row {
        display: flex;
        align-items: flex-end;
        gap: 4px;
    }

    // Plain `outlined dense` with a floating label, and no spacing overrides:
    // that is what every other form in the CRM uses, and matching it is the
    // whole point.
    &__field {
        flex-grow: 1;

        // `auto-grow` has no ceiling of its own, so a dictated-length
        // description would push the conversation off the panel.
        ::v-deep textarea {
            max-height: 150px;
            overflow-y: auto;
        }
    }

    // The row mirrors itself under `dir="rtl"`; the arrow inside does not.
    &__send-icon {
        @include for-hebrew {
            transform: scaleX(-1);
        }
    }

    &__recording {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 44px;
    }

    &__dot {
        width: 10px;
        height: 10px;
        flex-shrink: 0;
        border-radius: 50%;
        background: #da4453;
        animation: composer-pulse 1200ms ease-in-out infinite;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }

    &__timer {
        font-size: 13px;
        color: #55595c;
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
    }

    &__notice {
        margin: 0 0 8px;
        font-size: 12px;
        color: #da4453;
    }
}

@keyframes composer-pulse {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.3;
    }
}
</style>
